/**
 * Klaviyo renders its "Notify me" button into [klaviyo-bis-trigger] once, on page
 * load. The variant picker morphs the buy-buttons block, which replaces that
 * element and drops the injected button, so re-run Klaviyo's back in stock
 * initialisation after every variant change.
 */
(function () {
  const TRIGGER_SELECTOR = '[klaviyo-bis-trigger]';

  function reinitialize() {
    const trigger = document.querySelector(TRIGGER_SELECTOR);
    if (!trigger || trigger.children.length > 0) return;

    const klaviyo = window.klaviyo;
    if (!klaviyo || typeof klaviyo.enable !== 'function') return;

    klaviyo.enable('backinstock');
  }

  document.addEventListener('variant:update', () => {
    requestAnimationFrame(reinitialize);
  });

  // Theme Notify Me buttons stay hidden (see buy-buttons.liquid) unless the form is live. Klaviyo's
  // public forms feed only lists live forms, so a form in draft keeps them hidden.
  const config = /** @type {any} */ (window).klaviyoBisConfig;
  if (config) {
    fetch(`https://static-forms.klaviyo.com/forms/api/v7/${config.companyId}/full-forms`)
      .then((response) => response.json())
      .then((data) => {
        const live = data.full_forms?.some(
          (/** @type {any} */ form) => form.form_id === config.formId && form.live_form_versions?.length
        );
        if (live) document.documentElement.classList.add('klaviyo-bis-live');
      })
      .catch(() => {});
  }

  /** @type {string | null} */
  let urlBeforeCardForm = null;
  /** @type {string | undefined} */
  let cardFormId;
  let cardFormOpened = false;

  function restoreUrl() {
    if (!urlBeforeCardForm) return;
    history.replaceState(history.state, '', urlBeforeCardForm);
    urlBeforeCardForm = null;
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-klaviyo-bis-form-id]');
    if (!button) return;

    const klaviyo = window.klaviyo;
    if (!klaviyo || typeof klaviyo.openForm !== 'function') return;

    // Product cards: Klaviyo reads the product and variant from the page URL, so point it at the
    // card's product until the form closes
    const { productHandle } = button.dataset;
    const variantId = button.closest('.notify-me-trigger')?.dataset.variantId;
    if (productHandle && variantId) {
      urlBeforeCardForm = window.location.href;
      const productUrl = new URL(`/products/${productHandle}`, window.location.origin);
      productUrl.searchParams.set('variant', variantId);
      history.replaceState(history.state, '', productUrl);
      cardFormId = button.dataset.klaviyoBisFormId;
      cardFormOpened = false;
      // The form never opens while it's in draft in Klaviyo, so don't leave the URL changed
      setTimeout(() => {
        if (!cardFormOpened) restoreUrl();
      }, 3000);
    }

    klaviyo.openForm(button.dataset.klaviyoBisFormId);
  });

  window.addEventListener('klaviyoForms', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!urlBeforeCardForm || detail?.formId !== cardFormId) return;
    if (detail.type === 'open') cardFormOpened = true;
    if (detail.type === 'close') restoreUrl();
  });

  const BIS_EMAIL_SELECTOR = '[aria-label="Back in Stock Alerts"] input[name="email"]';

  /** @param {string} email @param {number} [attempt] */
  function prefillEmail(email, attempt = 0) {
    /** @type {HTMLInputElement | null} */
    const input = document.querySelector(BIS_EMAIL_SELECTOR);
    if (!input) {
      if (attempt < 20) requestAnimationFrame(() => prefillEmail(email, attempt + 1));
      return;
    }
    if (input.value) return;

    Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(input, email);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  window.addEventListener('klaviyoForms', (event) => {
    const email = /** @type {any} */ (window).klaviyoBisEmail;
    if (!email || /** @type {CustomEvent} */ (event).detail?.type !== 'open') return;
    prefillEmail(email);
  });

  const BIS_FORM_SELECTOR = '[aria-label="Back in Stock Alerts"] form.klaviyo-form';
  /** @type {HTMLElement | null} */
  let submittedEmailRow = null;

  /** @param {number} [attempt] */
  function showSubmittedEmail(attempt = 0) {
    const form = document.querySelector(BIS_FORM_SELECTOR);
    const buttonRow = form?.querySelector('.klaviyo-form-button')?.closest('[data-testid="form-row"]');
    if (!submittedEmailRow || !buttonRow || form?.querySelector('input[name="email"]')) {
      if (attempt < 300) requestAnimationFrame(() => showSubmittedEmail(attempt + 1));
      return;
    }
    buttonRow.before(submittedEmailRow);
    submittedEmailRow = null;
  }

  /** @param {Event} event */
  function copyEmailRow(event) {
    const form = /** @type {HTMLElement} */ (event.target).closest?.(BIS_FORM_SELECTOR);
    const input = /** @type {HTMLInputElement | null | undefined} */ (form?.querySelector('input[name="email"]'));
    if (!input?.value) return;

    const row = /** @type {HTMLElement | null} */ (input.closest('[data-testid="form-row"]')?.cloneNode(true));
    const copy = row?.querySelector('input');
    if (!row || !copy) return;
    copy.removeAttribute('name');
    copy.removeAttribute('id');
    copy.value = input.value;
    copy.readOnly = true;
    copy.tabIndex = -1;
    submittedEmailRow = row;
    showSubmittedEmail();
  }

  document.addEventListener(
    'click',
    (event) => {
      if (/** @type {HTMLElement} */ (event.target).closest?.('.klaviyo-form-button')) copyEmailRow(event);
    },
    true
  );
  document.addEventListener('submit', copyEmailRow, true);

  const BIS_CLOSE_SELECTOR = '[aria-label="Back in Stock Alerts"] .klaviyo-close-form';
  const closeBisForm = () => /** @type {HTMLElement | null} */ (document.querySelector(BIS_CLOSE_SELECTOR))?.click();

  window.addEventListener('klaviyoForms', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.type !== 'submit' || detail.formId !== config?.formId) return;
    setTimeout(closeBisForm, 2900);
  });

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches?.('[aria-label="Back in Stock Alerts"] [data-testid="form-row"]:has([data-color="#dff0d8" i]) p')) {
      closeBisForm();
    }
  });
})();
